import * as React from 'react';
import { getAllAvailableTimeslotsPerHour } from '../state/availableTimeCentricTimeslotHelpers';
import {
    AvailablePhotographer,
    AvailableTimeslot,
    AvailableTimeslotWithPhotographers
} from '../../types';
import { useBookingCalendarHeightOffset } from '../../state/useHeightOffsetHelper';
import { BookingDateSelect } from '../../bookingDateSelect/view/BookingDateSelect';
import { AvailableTimeCentricTimeslotSection } from './AvailableTimeCentricTimeslotSection';
import { TimeCentricBookingFooter } from './TimeCentricBookingFooter';
import { NoPhotographersWithTimeslotsAvailable } from './NoPhotographersWithTimeslotsAvailable';

type TimeslotCentricProps = {
    allAvailablePhotographers: AvailablePhotographer[];
    displayingBookingDate: Date;
    handleBookFactory: (
        planOrderId: number | null,
        selectedPhotographerIndex: number
    ) => (timeslot: string) => void;
    onChangeDisplayingBookingDate: (dateTime: Date) => void;
    planOrderId: number | null;
    showHints: boolean;
    earliestBookableDate?: Date | null;
};

export function TimeslotCentric({
    allAvailablePhotographers,
    displayingBookingDate,
    handleBookFactory,
    onChangeDisplayingBookingDate,
    planOrderId,
    showHints,
    earliestBookableDate = null
}: TimeslotCentricProps) {
    let [showFooter, setShowFooter] = React.useState<boolean>(false);
    let [selectedBookingDate, setSelectedBookingDate] = React.useState<string | null>(null);
    let [selectedTimeslot, setSelectedTimeslot] = React.useState<AvailableTimeslot | null>(null);
    let [availablePhotographers, setAvailablePhotographers] = React.useState<
        AvailablePhotographer[]
    >([]);
    let [selectedPhotographer, setSelectedPhotographer] =
        React.useState<AvailablePhotographer | null>(null);

    let { heightOffset, footerRef } = useBookingCalendarHeightOffset(showFooter);

    if (availablePhotographers.length === 1 && !selectedPhotographer) {
        setSelectedPhotographer(availablePhotographers[0]);
    }
    function handleCancelBook() {
        setShowFooter(false);

        // Delay a bit so that the footer can animate out before changing ui
        setTimeout(() => {
            setSelectedBookingDate(null);
            setSelectedTimeslot(null);
            setSelectedPhotographer(null);
        }, 0);
    }

    function handleChangeTimeslotFromFooter(
        timeslot: AvailableTimeslot,
        photographers: AvailablePhotographer[]
    ) {
        setSelectedTimeslot(timeslot);
        setAvailablePhotographers(photographers);
    }

    function handleChangeTimeslot(timeslot: string) {
        if (allAvailablePhotographers.length < 1) {
            return;
        }

        setSelectedBookingDate(timeslot);
        setSelectedTimeslot(null);

        if (!showFooter) {
            setShowFooter(true);
        }
    }

    function handleChangePhotographer(photographerId: number) {
        let photographer = allAvailablePhotographers.find((p) => p.id === photographerId);
        if (photographer) {
            setSelectedPhotographer(photographer);
        }
    }

    let availableTimeslotsWithPhotographerPerHour =
        getAllAvailableTimeslotsPerHour(allAvailablePhotographers);

    let availableTimeslotsPerHour = new Map<string, AvailableTimeslotWithPhotographers[]>();

    for (let [
        hour,
        availableTimeslotsWithPhotographers
    ] of availableTimeslotsWithPhotographerPerHour) {
        let currentHour = availableTimeslotsPerHour.get(hour);

        if (!currentHour) {
            let availableSlots = [] as AvailableTimeslotWithPhotographers[];

            for (let [, timeslotWithPhotographers] of availableTimeslotsWithPhotographers) {
                availableSlots.push(timeslotWithPhotographers);
            }

            availableTimeslotsPerHour.set(hour, availableSlots);
        }
    }

    let [selectedHour] = selectedBookingDate ? selectedBookingDate.split(':') : [null, null];
    let availableTimeslotsForSelectedHour = availableTimeslotsPerHour.get(selectedHour) || [];

    let selectedForecast =
        availableTimeslotsForSelectedHour.length > 0
            ? availableTimeslotsForSelectedHour[0].timeslot.forecast
            : null;

    return (
        <>
            <BookingDateSelect
                showHint={showHints}
                disabled={showFooter}
                bookingDate={displayingBookingDate}
                onChange={(newDate: Date) => {
                    onChangeDisplayingBookingDate(newDate);
                }}
                earliestBookableDate={earliestBookableDate}
            />
            {availableTimeslotsPerHour.size < 1 ? (
                <NoPhotographersWithTimeslotsAvailable />
            ) : (
                <AvailableTimeCentricTimeslotSection
                    selectedDifferentTime={selectedTimeslot}
                    showHint={showHints}
                    availableTimeslotsPerHour={availableTimeslotsPerHour}
                    bottomOffset={heightOffset}
                    selectedTimeslot={selectedBookingDate}
                    onChange={handleChangeTimeslot}
                />
            )}
            <TimeCentricBookingFooter
                ref={footerRef}
                key={selectedBookingDate}
                availablePhotographers={availablePhotographers}
                availableTimeslots={availableTimeslotsForSelectedHour}
                forecast={selectedForecast}
                isOpen={showFooter}
                selectedDate={selectedBookingDate}
                selectedTimeslot={selectedTimeslot}
                onChangeTimeslot={handleChangeTimeslotFromFooter}
                onChangePhotographer={handleChangePhotographer}
                selectedPhotographer={selectedPhotographer}
                onCancel={handleCancelBook}
                onBook={handleBookFactory(
                    planOrderId,
                    selectedPhotographer ? selectedPhotographer.id : null
                )}
            />
        </>
    );
}
